import {
  Dialog,
  Flex,
  Heading,
  Kbd,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useIsLegacyPlan } from '~publish/contexts/AccountContext'
import { useHasAccessToBufferHome } from '~publish/hooks/useHasAccessToBufferHome'

import styles from './KeyboardShortcutsDialog.module.css'

import type { JSX } from 'react'

type Shortcut = {
  label: string
  keys: string[][]
  alternativeKeys?: string[][]
}

type ShortcutGroup = {
  id: string
  title: string
  shortcuts: Shortcut[]
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    id: 'general',
    title: 'General',
    shortcuts: [
      { label: 'Open Quick Navigator', keys: [['mod', 'K']] },
      { label: 'Toggle Sidebar', keys: [['mod', '\\']] },
      {
        label: 'Keyboard Shortcuts',
        keys: [['?']],
        alternativeKeys: [['mod', '/']],
      },
    ],
  },
  {
    id: 'publish',
    title: 'Publish',
    shortcuts: [
      { label: 'New Post', keys: [['N'], ['P']] },
      { label: 'New Idea', keys: [['N'], ['I']] },
      { label: 'Connect Channel', keys: [['N'], ['C']] },
    ],
  },
  {
    id: 'navigation',
    title: 'Navigation',
    shortcuts: [
      { label: 'Go to Home', keys: [['G'], ['H']] },
      { label: 'Go to Publish', keys: [['G'], ['P']] },
      { label: 'Go to Analytics', keys: [['G'], ['A']] },
      { label: 'Go to Community', keys: [['G'], ['C']] },
      { label: 'Go to Start Page', keys: [['G'], ['S']] },
      { label: 'Go to Ideas', keys: [['G'], ['I']] },
    ],
  },
  {
    id: 'composer',
    title: 'Composer',
    shortcuts: [
      { label: 'Save Draft', keys: [['mod', 'S']] },
      { label: 'Schedule / Update Post', keys: [['mod', 'enter']] },
      {
        label: 'Schedule & Create New',
        keys: [['mod', 'shift', 'enter']],
      },
      { label: 'Toggle Maximize Composer', keys: [['mod', 'shift', 'F']] },
    ],
  },
  {
    id: 'community',
    title: 'Community',
    shortcuts: [
      { label: 'Previous Comment', keys: [['↑']] },
      { label: 'Next Comment', keys: [['↓']] },
      { label: 'Submit Reply', keys: [['mod', 'enter']] },
    ],
  },
]

const screenReaderKeyMap: Record<string, string> = {
  mod: navigator.userAgent.includes('Macintosh') ? 'Command' : 'Control',
  alt: navigator.userAgent.includes('Macintosh') ? 'Option' : 'Alt',
  shift: 'Shift',
  enter: 'Enter',
  control: 'Control',
  del: 'Delete',
  tab: 'Tab',
  '↑': 'Up Arrow',
  '↓': 'Down Arrow',
  '\\': 'Backslash',
}

function mapKeyForScreenReader(key: string): string {
  return screenReaderKeyMap[key.toLowerCase()] ?? key
}

function formatKeysForScreenReader(keys: string[][]): string {
  return keys
    .map((combo) => combo.map(mapKeyForScreenReader).join(' + '))
    .join(' then ')
}

function KeyCombo({ keys }: { keys: string[][] }): JSX.Element {
  return (
    <>
      {keys.map((combo, index) => (
        <Flex key={combo.join('+')} align="center" gap="2xs" aria-hidden="true">
          {index > 0 && <span className={styles.separator}>then</span>}
          {combo.map((key) => (
            <Kbd key={key}>{key}</Kbd>
          ))}
        </Flex>
      ))}
    </>
  )
}

function ShortcutKeys({
  keys,
  alternativeKeys,
}: {
  keys: string[][]
  alternativeKeys?: string[][]
}): JSX.Element {
  const srLabel = alternativeKeys
    ? `${formatKeysForScreenReader(keys)} or ${formatKeysForScreenReader(
        alternativeKeys,
      )}`
    : formatKeysForScreenReader(keys)

  return (
    <Flex
      align="center"
      gap="2xs"
      className={styles.keys}
      aria-label={srLabel}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The related key combinations need a single accessible group name.
      role="group"
    >
      <KeyCombo keys={keys} />
      {alternativeKeys && (
        <>
          <span className={styles.separator} aria-hidden="true">
            or
          </span>
          <KeyCombo keys={alternativeKeys} />
        </>
      )}
    </Flex>
  )
}

function ShortcutSection({
  group,
  hiddenLabels,
}: {
  group: ShortcutGroup
  hiddenLabels: ReadonlySet<string>
}): JSX.Element {
  const headingId = `shortcuts-group-${group.id}`

  // Drop shortcuts whose handler is gated off for this user so the dialog never
  // advertises a dead key (e.g. G H without Buffer Home, N I on legacy plans).
  const shortcuts = hiddenLabels.size
    ? group.shortcuts.filter((shortcut) => !hiddenLabels.has(shortcut.label))
    : group.shortcuts

  return (
    <section aria-labelledby={headingId} className={styles.group}>
      <Heading size="xs" id={headingId}>
        {group.title}
      </Heading>
      <dl className={styles.list}>
        {shortcuts.map((shortcut) => (
          <div key={shortcut.label} className={styles.shortcutRow}>
            <dt className={styles.shortcutLabel}>{shortcut.label}</dt>
            <dd className={styles.shortcutValue}>
              <ShortcutKeys
                keys={shortcut.keys}
                alternativeKeys={shortcut.alternativeKeys}
              />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export function KeyboardShortcutsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}): JSX.Element {
  const { hasAccess } = useHasAccessToBufferHome()
  const isLegacyPlan = useIsLegacyPlan()

  // Mirror the handler gates in useNavigationShortcuts so the dialog only
  // advertises shortcuts that actually navigate for this user.
  const hiddenLabels = new Set<string>()
  if (!hasAccess) hiddenLabels.add('Go to Home')
  if (isLegacyPlan) hiddenLabels.add('New Idea')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium" className={styles.content}>
        <Dialog.Header>
          <Dialog.Title>Keyboard Shortcuts</Dialog.Title>
          <VisuallyHidden>
            <Dialog.Description>
              A reference of all available keyboard shortcuts grouped by
              category.
            </Dialog.Description>
          </VisuallyHidden>
          <Dialog.CloseButton />
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          {SHORTCUT_GROUPS.map((group) => (
            <ShortcutSection
              key={group.id}
              group={group}
              hiddenLabels={hiddenLabels}
            />
          ))}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
